import classNames from 'classnames';
import {List} from 'immutable';
import React, {useState} from 'react';
import {useShallow} from 'zustand/react/shallow';

import ClassroomInvitation from './ClassroomInvitation';
import {ClassroomInvitationModelV1} from '@albert-io/models';
import {getClassroomInvitationsQuery} from 'lib/ClassroomInvitationUtil/ClassroomInvitationUtil';
import {LoadingSpinner} from '@albert-io/atomic';
import useOnResourceStale from './hooks/useOnResourceStale';
import {useStudentDashboard} from 'client/Classrooms/StudentDashboard/StudentDashboard.store';

interface Props extends PropsWithClassNameOptional {}

export default function ClassroomInvitations({className}: Props) {
  const {isReloadingStudent, studentEmail} = useStudentDashboard(
    useShallow((state) => ({
      isReloadingStudent: state.isReloadingStudent,
      studentEmail: state.student?.getEmail() ?? ''
    }))
  );

  const [classroomInvitationsLastFetchedAt, setClassroomInvitationsLastFetchedAt] = useState(0);
  const [invitations, setInvitations] = useState(List<ClassroomInvitationModelV1>());
  const [isLoading, setIsLoading] = useState(false);

  useOnResourceStale(classroomInvitationsLastFetchedAt, () => {
    const query = getClassroomInvitationsQuery(studentEmail);

    setIsLoading(true);
    query.invalidateInterest();
    setClassroomInvitationsLastFetchedAt(Date.now());

    query
      .getResourcePromise()
      .then((fetchedInvitations) => {
        setInvitations(fetchedInvitations);
      })
      .catch((error) => {
        console.error(error);
        // TODO: Add better error handling
      })
      .finally(() => {
        setIsLoading(false);
      });
  });

  if (!isLoading && invitations.size === 0) {
    return null;
  }

  return isReloadingStudent || isLoading ? (
    <LoadingSpinner bottomMarginLevel={0} size={2} />
  ) : (
    <ul className={classNames(className, 'flex list-none flex-col gap-x1 p-x0')}>
      {invitations.map((invitation) => (
        <ClassroomInvitation invitation={invitation} key={invitation.getId()} />
      ))}
    </ul>
  );
}
